/**
 * Terrain Rasterization — primitive shape rasterization + edge detection.
 *
 * Used by terrain generation to rasterize wireframe layouts into the grid.
 */

import { TERRAIN, CELL } from '../constants';
import type { WireframeLayout } from '../types';
import { getGrid, worldToGridX, worldToGridY } from './grid-core';
import type { GridBounds } from './grid-destruction';

const { CELL_SIZE } = TERRAIN;

// ─── Rasterization primitives ───────────────────────────────────────────────────

/** Fill an axis-aligned rectangle with given cell value. World coords. */
function fillGridRect(wx: number, wy: number, wWidth: number, wHeight: number, value: number): void {
  const grid = getGrid();
  if (!grid) return;
  const minGx = Math.max(0, worldToGridX(wx));
  const minGy = Math.max(0, worldToGridY(wy));
  const maxGx = Math.min(TERRAIN.GRID_WIDTH - 1, worldToGridX(wx + wWidth));
  const maxGy = Math.min(TERRAIN.GRID_HEIGHT - 1, worldToGridY(wy + wHeight));

  for (let gy = minGy; gy <= maxGy; gy++) {
    const rowOffset = gy * TERRAIN.GRID_WIDTH;
    for (let gx = minGx; gx <= maxGx; gx++) {
      grid[rowOffset + gx] = value;
    }
  }
}

/** Fill a circle with given cell value. World coords center, radius in grid cells. */
function fillGridCircle(wcx: number, wcy: number, gridRadius: number, value: number): void {
  const grid = getGrid();
  if (!grid) return;
  const gcx = wcx / CELL_SIZE;
  const gcy = wcy / CELL_SIZE;
  const rSq = gridRadius * gridRadius;

  const minGx = Math.max(0, Math.floor(gcx - gridRadius));
  const maxGx = Math.min(TERRAIN.GRID_WIDTH - 1, Math.ceil(gcx + gridRadius));
  const minGy = Math.max(0, Math.floor(gcy - gridRadius));
  const maxGy = Math.min(TERRAIN.GRID_HEIGHT - 1, Math.ceil(gcy + gridRadius));

  for (let gy = minGy; gy <= maxGy; gy++) {
    const dy = gy - gcy;
    const rowOffset = gy * TERRAIN.GRID_WIDTH;
    for (let gx = minGx; gx <= maxGx; gx++) {
      const dx = gx - gcx;
      if (dx * dx + dy * dy <= rSq) {
        grid[rowOffset + gx] = value;
      }
    }
  }
}

/** Fill a thick line with given cell value. World coords, thickness in grid cells. */
function fillGridThickLine(wx1: number, wy1: number, wx2: number, wy2: number, gridThickness: number, value: number): void {
  const grid = getGrid();
  if (!grid) return;
  const gx1 = wx1 / CELL_SIZE;
  const gy1 = wy1 / CELL_SIZE;
  const gx2 = wx2 / CELL_SIZE;
  const gy2 = wy2 / CELL_SIZE;

  const dx = gx2 - gx1;
  const dy = gy2 - gy1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 0.001) {
    // Degenerate — fill a circle at the point
    fillGridCircle(wx1, wy1, gridThickness, value);
    return;
  }

  const halfThick = gridThickness / 2;
  // Step along the line and fill circles at each point
  const steps = Math.ceil(len);
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const px = gx1 + dx * t;
    const py = gy1 + dy * t;

    // Fill a circle at this point (grid coords)
    const minGx = Math.max(0, Math.floor(px - halfThick));
    const maxGx = Math.min(TERRAIN.GRID_WIDTH - 1, Math.ceil(px + halfThick));
    const minGy = Math.max(0, Math.floor(py - halfThick));
    const maxGy = Math.min(TERRAIN.GRID_HEIGHT - 1, Math.ceil(py + halfThick));
    const rSq = halfThick * halfThick;

    for (let gy = minGy; gy <= maxGy; gy++) {
      const ddy = gy - py;
      const rowOffset = gy * TERRAIN.GRID_WIDTH;
      for (let gx = minGx; gx <= maxGx; gx++) {
        const ddx = gx - px;
        if (ddx * ddx + ddy * ddy <= rSq) {
          grid[rowOffset + gx] = value;
        }
      }
    }
  }
}

/** Rasterize a complete WireframeLayout to the grid.
 *  Ground segments use SAND (fully destructible, no wireframe foundations).
 *  Floating platforms stay as WIREFRAME (distinct visual identity). */
export function rasterizeWireframe(layout: WireframeLayout): void {
  // 1. Ground segments — sand (no hidden wireframe beneath terrain)
  for (const seg of layout.groundSegments) {
    fillGridRect(seg.x, seg.surfaceY, seg.width, seg.depth, CELL.SAND);
  }

  // 2. Floating platforms — wireframe
  for (const plat of layout.platforms) {
    fillGridRect(plat.x, plat.y, plat.width, plat.thickness * CELL_SIZE, CELL.WIREFRAME);
  }

  // 3. Beams
  for (const beam of layout.beams) {
    const from = layout.nodes[beam.from];
    const to = layout.nodes[beam.to];
    fillGridThickLine(from.x, from.y, to.x, to.y, beam.thickness, CELL.WIREFRAME);
  }

  // 4. Nodes
  for (const node of layout.nodes) {
    fillGridCircle(node.x, node.y, node.radius, CELL.WIREFRAME);
  }
}

// ─── Contour polyline extraction ─────────────────────────────────────────────────

export interface ContourPolyline {
  points: number[];  // flat [x0, y0, x1, y1, ...] in world coords
}

export interface ContourCache {
  sand: ContourPolyline[];
  wire: ContourPolyline[];
}

/** Build contour polylines from edge cells.
 *  For each edge cell, boundary sides (bordering empty space) emit line segments.
 *  Connected segments are chained into polylines, separated by material.
 *  Collinear runs are simplified (middle points removed). */
export function buildContourPolylines(edgeCells: [number, number][]): ContourCache {
  const grid = getGrid();
  if (!grid) return { sand: [], wire: [] };

  // Step 1: Extract boundary segments per material
  // A segment is two endpoints: [x0, y0, x1, y1]
  const sandSegs: [number, number, number, number][] = [];
  const wireSegs: [number, number, number, number][] = [];

  for (const [gx, gy] of edgeCells) {
    const cellVal = grid[gy * TERRAIN.GRID_WIDTH + gx];
    if (cellVal === 0) continue;
    const segs = cellVal === CELL.WIREFRAME ? wireSegs : sandSegs;

    const wx = gx * CELL_SIZE;
    const wy = gy * CELL_SIZE;
    const wx1 = wx + CELL_SIZE;
    const wy1 = wy + CELL_SIZE;

    // Top side — empty above
    if (gy === 0 || grid[(gy - 1) * TERRAIN.GRID_WIDTH + gx] === 0) {
      segs.push([wx, wy, wx1, wy]);
    }
    // Bottom side — empty below
    if (gy === TERRAIN.GRID_HEIGHT - 1 || grid[(gy + 1) * TERRAIN.GRID_WIDTH + gx] === 0) {
      segs.push([wx, wy1, wx1, wy1]);
    }
    // Left side — empty left
    if (gx === 0 || grid[gy * TERRAIN.GRID_WIDTH + gx - 1] === 0) {
      segs.push([wx, wy, wx, wy1]);
    }
    // Right side — empty right
    if (gx === TERRAIN.GRID_WIDTH - 1 || grid[gy * TERRAIN.GRID_WIDTH + gx + 1] === 0) {
      segs.push([wx1, wy, wx1, wy1]);
    }
  }

  return {
    sand: chainAndSimplify(sandSegs, 4),  // organic — smooth curves
    wire: chainAndSimplify(wireSegs, 0),  // structural — keep sharp geometric edges
  };
}

/** Chain connected segments into polylines, simplify collinear runs, optionally smooth. */
function chainAndSimplify(segments: [number, number, number, number][], smoothIterations: number): ContourPolyline[] {
  if (segments.length === 0) return [];

  // Build adjacency map: endpoint → list of segment indices
  const adj = new Map<string, number[]>();
  const key = (x: number, y: number) => `${x},${y}`;

  for (let i = 0; i < segments.length; i++) {
    const [x0, y0, x1, y1] = segments[i];
    const k0 = key(x0, y0);
    const k1 = key(x1, y1);
    let list = adj.get(k0);
    if (!list) { list = []; adj.set(k0, list); }
    list.push(i);
    list = adj.get(k1);
    if (!list) { list = []; adj.set(k1, list); }
    list.push(i);
  }

  // Walk chains
  const used = new Uint8Array(segments.length);
  const polylines: ContourPolyline[] = [];

  for (let si = 0; si < segments.length; si++) {
    if (used[si]) continue;
    used[si] = 1;

    const [x0, y0, x1, y1] = segments[si];
    // Build chain in both directions from this seed segment
    const chain: number[] = [x0, y0, x1, y1];

    // Extend forward from (x1, y1)
    let cx = x1, cy = y1;
    for (;;) {
      const neighbors = adj.get(key(cx, cy));
      if (!neighbors) break;
      let found = false;
      for (const ni of neighbors) {
        if (used[ni]) continue;
        used[ni] = 1;
        const [nx0, ny0, nx1, ny1] = segments[ni];
        if (nx0 === cx && ny0 === cy) {
          chain.push(nx1, ny1);
          cx = nx1; cy = ny1;
        } else {
          chain.push(nx0, ny0);
          cx = nx0; cy = ny0;
        }
        found = true;
        break;
      }
      if (!found) break;
    }

    // Extend backward from (x0, y0) — prepend
    cx = x0; cy = y0;
    for (;;) {
      const neighbors = adj.get(key(cx, cy));
      if (!neighbors) break;
      let found = false;
      for (const ni of neighbors) {
        if (used[ni]) continue;
        used[ni] = 1;
        const [nx0, ny0, nx1, ny1] = segments[ni];
        if (nx0 === cx && ny0 === cy) {
          chain.unshift(nx1, ny1);
          cx = nx1; cy = ny1;
        } else {
          chain.unshift(nx0, ny0);
          cx = nx0; cy = ny0;
        }
        found = true;
        break;
      }
      if (!found) break;
    }

    // Step 3: Simplify collinear runs — remove middle points where 3 consecutive
    // share the same X or same Y
    const simplified: number[] = [chain[0], chain[1]];
    for (let i = 2; i < chain.length - 2; i += 2) {
      const px = simplified[simplified.length - 2];
      const py = simplified[simplified.length - 1];
      const cx2 = chain[i], cy2 = chain[i + 1];
      const nx = chain[i + 2], ny = chain[i + 3];
      // Collinear if all 3 share X or all 3 share Y
      if ((px === cx2 && cx2 === nx) || (py === cy2 && cy2 === ny)) {
        continue; // skip middle point
      }
      simplified.push(cx2, cy2);
    }
    // Always add last point
    if (chain.length >= 4) {
      simplified.push(chain[chain.length - 2], chain[chain.length - 1]);
    }

    // Step 4: Anchor sharp corners, then Chaikin smooth (sand only — wireframe keeps sharp geometry)
    if (smoothIterations > 0) {
      const anchored = anchorSharpCorners(simplified, CELL_SIZE * 4);
      const smoothed = chaikinSmooth(anchored, smoothIterations);
      if (smoothed.length >= 4) polylines.push({ points: smoothed });
    } else {
      if (simplified.length >= 4) polylines.push({ points: simplified });
    }
  }

  return polylines;
}

/** Insert anchor points near sharp corners to prevent Chaikin from cutting them.
 *  At corners where both adjacent segments exceed `threshold`, insert points at
 *  90% and 10% along incoming/outgoing segments. This pins the curve close to
 *  the corner. Short staircase segments (below threshold) are left alone. */
function anchorSharpCorners(points: number[], threshold: number): number[] {
  if (points.length < 6) return points;
  const out: number[] = [points[0], points[1]];
  for (let i = 2; i < points.length - 2; i += 2) {
    const px = points[i - 2], py = points[i - 1];
    const cx = points[i], cy = points[i + 1];
    const nx = points[i + 2], ny = points[i + 3];
    const prevLen = Math.sqrt((cx - px) * (cx - px) + (cy - py) * (cy - py));
    const nextLen = Math.sqrt((nx - cx) * (nx - cx) + (ny - cy) * (ny - cy));
    if (prevLen > threshold && nextLen > threshold) {
      // Anchor: insert point near corner on incoming side
      out.push(px + (cx - px) * 0.9, py + (cy - py) * 0.9);
      out.push(cx, cy);
      // Anchor: insert point near corner on outgoing side
      out.push(cx + (nx - cx) * 0.1, cy + (ny - cy) * 0.1);
    } else {
      out.push(cx, cy);
    }
  }
  out.push(points[points.length - 2], points[points.length - 1]);
  return out;
}

/** Chaikin's corner-cutting subdivision. Each iteration replaces each corner
 *  with two new points at 25% and 75% along adjacent segments.
 *  Endpoints are preserved to prevent contour shrinkage. */
function chaikinSmooth(points: number[], iterations: number): number[] {
  let pts = points;
  for (let iter = 0; iter < iterations; iter++) {
    if (pts.length < 6) break; // need at least 3 points to smooth
    const out: number[] = [pts[0], pts[1]]; // preserve first point
    for (let i = 0; i < pts.length - 2; i += 2) {
      const x0 = pts[i], y0 = pts[i + 1];
      const x1 = pts[i + 2], y1 = pts[i + 3];
      // Q = 0.75 * P0 + 0.25 * P1
      out.push(x0 * 0.75 + x1 * 0.25, y0 * 0.75 + y1 * 0.25);
      // R = 0.25 * P0 + 0.75 * P1
      out.push(x0 * 0.25 + x1 * 0.75, y0 * 0.25 + y1 * 0.75);
    }
    out.push(pts[pts.length - 2], pts[pts.length - 1]); // preserve last point
    pts = out;
  }
  return pts;
}

// ─── Edge detection ─────────────────────────────────────────────────────────────

/** Find edge cells — full grid scan or bounded region.
 *  When bounds are provided, only scans within the clamped region. */
export function findEdgeCells(bounds?: GridBounds): [number, number][] {
  const grid = getGrid();
  if (!grid) return [];

  const startGx = bounds ? Math.max(0, bounds.minGx) : 0;
  const endGx = bounds ? Math.min(TERRAIN.GRID_WIDTH - 1, bounds.maxGx) : TERRAIN.GRID_WIDTH - 1;
  const startGy = bounds ? Math.max(0, bounds.minGy) : 0;
  const endGy = bounds ? Math.min(TERRAIN.GRID_HEIGHT - 1, bounds.maxGy) : TERRAIN.GRID_HEIGHT - 1;
  const edges: [number, number][] = [];

  for (let gy = startGy; gy <= endGy; gy++) {
    const rowOffset = gy * TERRAIN.GRID_WIDTH;
    for (let gx = startGx; gx <= endGx; gx++) {
      if (grid[rowOffset + gx] === 0) continue;

      const hasEmptyNeighbor =
        (gx > 0 && grid[rowOffset + gx - 1] === 0) ||
        (gx < TERRAIN.GRID_WIDTH - 1 && grid[rowOffset + gx + 1] === 0) ||
        (gy > 0 && grid[(gy - 1) * TERRAIN.GRID_WIDTH + gx] === 0) ||
        (gy < TERRAIN.GRID_HEIGHT - 1 && grid[(gy + 1) * TERRAIN.GRID_WIDTH + gx] === 0) ||
        gx === 0 || gx === TERRAIN.GRID_WIDTH - 1 || gy === 0 || gy === TERRAIN.GRID_HEIGHT - 1;

      if (hasEmptyNeighbor) {
        edges.push([gx, gy]);
      }
    }
  }

  return edges;
}
